import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ReuseDialog from './ReuseDialog';
import SongLinkPicker from './SongLinkPicker';
import { detachVersion, errorMessage, getLibrarySetlist, setItemLink, setLibraryStatus } from './libraryApi';
import { formatServiceDate, pluralize, STATUS_LABELS, STATUS_STYLES } from './libraryFormat';

const MatchState = ({ item, canManage, onLink, onUnlink, onPick, picking, setPicking }) => {
  if (item.link_broken) {
    return <div className="text-xs text-red-700">Koppeling verbroken (lied bestaat niet meer)
      {canManage && <button type="button" className="ml-2 min-h-11 underline" onClick={() => setPicking(item.id)}>Opnieuw koppelen</button>}</div>;
  }
  if (item.song_id) {
    return <div className="text-xs text-emerald-800">Gekoppeld aan {item.song_title}
      {canManage && <button type="button" className="ml-2 min-h-11 underline" onClick={() => onUnlink(item)} aria-label={`Koppeling van ${item.original_title} verwijderen`}>Ontkoppelen</button>}</div>;
  }
  return (
    <div className="space-y-1 text-xs text-amber-800">
      <span>Niet gekoppeld{item.suggestions?.length ? ' — voorstel:' : ''}</span>
      {canManage && item.suggestions?.map((suggestion) => (
        <button key={suggestion.song_id} type="button" onClick={() => onLink(item, suggestion.song_id)} aria-label={`Bevestig koppeling ${item.original_title} aan ${suggestion.title}`}
          className="ml-2 min-h-11 rounded-md border border-amber-300 bg-white px-2 font-medium">{suggestion.title} ({suggestion.reason.toLowerCase()}) bevestigen</button>
      ))}
      {canManage && <button type="button" className="ml-2 min-h-11 underline" onClick={() => setPicking(item.id)} aria-label={`Koppel ${item.original_title} handmatig`}>Handmatig koppelen</button>}
      {picking === item.id && <SongLinkPicker initialQuery={item.original_title} onPick={(song) => onPick(item, song)} onCancel={() => setPicking(null)} />}
    </div>
  );
};

const VersionDiff = ({ version }) => {
  if (!version.diff) return null;
  const { added, removed, moved, identical } = version.diff;
  if (identical) return <p className="text-xs text-slate-500">Identiek aan de geopende versie</p>;
  return (
    <ul className="space-y-0.5 text-xs text-slate-600">
      {added.length > 0 && <li>Extra t.o.v. geopend: {added.join(', ')}</li>}
      {removed.length > 0 && <li>Ontbreekt t.o.v. geopend: {removed.join(', ')}</li>}
      {moved.length > 0 && <li>Andere plek: {moved.join(', ')}</li>}
      {!added.length && !removed.length && !moved.length && <li>Alleen schrijfwijze verschilt</li>}
    </ul>
  );
};

/** Full song order of one historical setlist, its versions, and the actions that belong to it. */
const SetlistDetail = ({ id, canManage, onClose, onChanged, onReused }) => {
  const [openId, setOpenId] = useState(id);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [picking, setPicking] = useState(null);
  const [reuseOpen, setReuseOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setOpenId(id); }, [id]);
  const load = useCallback(() => getLibrarySetlist(openId).then((data) => { setDetail(data); setError(''); })
    .catch((err) => setError(errorMessage(err, 'Setlist laden mislukt'))), [openId]);
  useEffect(() => { if (openId) { setDetail(null); load(); } }, [openId, load]);

  const run = async (action, success) => {
    setBusy(true);
    try {
      const next = await action();
      setDetail(next);
      if (success) toast.success(success);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, 'Wijzigen mislukt'));
    } finally {
      setBusy(false);
    }
  };

  const link = (item, songId) => run(() => setItemLink(detail.id, item.id, songId), songId ? 'Koppeling opgeslagen' : 'Koppeling verwijderd');
  const pick = (item, song) => { setPicking(null); return link(item, song.id); };

  return (
    <>
      <Dialog open={!!id} onOpenChange={(value) => { if (!value) onClose(); }}>
        <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail ? (detail.occasion || 'Zangdienst') : 'Setlist'}</DialogTitle>
            <DialogDescription>{detail ? `${formatServiceDate(detail.service_date, detail.date_certain)} · ${pluralize(detail.item_count, 'lied', 'liederen')}` : 'Laden…'}</DialogDescription>
          </DialogHeader>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          {detail && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={STATUS_STYLES[detail.status]}>{STATUS_LABELS[detail.status]}</Badge>
                {detail.version_count > 1 && <Badge variant="secondary">Versie {detail.version_number} van {detail.version_count}</Badge>}
                {canManage && (
                  <select aria-label="Status wijzigen" className="h-11 rounded-md border border-slate-200 bg-white px-3 text-sm" value={detail.status} disabled={busy}
                    onChange={(event) => run(() => setLibraryStatus(detail.id, event.target.value), 'Status bijgewerkt')}>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                )}
                {canManage && <Button type="button" className="ml-auto min-h-11 bg-teal-600 text-white hover:bg-teal-700" onClick={() => setReuseOpen(true)}>Gebruik als nieuwe setlist</Button>}
              </div>

              <ol aria-label="Liedvolgorde" className="space-y-2">
                {detail.items.map((item) => (
                  <li key={item.id} className="flex gap-3 rounded-md border border-slate-200 p-3">
                    <span className="w-6 shrink-0 text-right font-semibold text-slate-400">{item.order}</span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="break-words font-medium text-slate-900">{item.original_title}</p>
                      <p className="text-xs text-slate-500">
                        {[item.key && `Toonsoort ${item.key}`, item.opwekking_number && `Opwekking ${item.opwekking_number}`, item.note].filter(Boolean).join(' · ')}
                      </p>
                      <MatchState item={item} canManage={canManage} onLink={link} onUnlink={(entry) => link(entry, null)} onPick={pick} picking={picking} setPicking={setPicking} />
                    </div>
                  </li>
                ))}
              </ol>

              {detail.versions.length > 1 && (
                <section aria-label="Versies" className="space-y-2">
                  <h3 className="font-semibold text-slate-900">Versies van deze zangdienst</h3>
                  <p className="text-xs text-slate-500">Versies worden niet samengevoegd; alle oorspronkelijke lijsten blijven bewaard.</p>
                  <ul className="space-y-2">
                    {detail.versions.map((version) => (
                      <li key={version.id} className={`rounded-md border p-3 ${version.id === detail.id ? 'border-teal-300 bg-teal-50' : 'border-slate-200'}`}>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium">Versie {version.version_number}{version.version_label ? ` (${version.version_label})` : ''}</span>
                          <Badge className={STATUS_STYLES[version.status]}>{STATUS_LABELS[version.status]}</Badge>
                          <span className="text-slate-500">{pluralize(version.item_count, 'lied', 'liederen')}</span>
                          <div className="ml-auto flex flex-wrap gap-2">
                            {version.id !== detail.id && <Button type="button" variant="outline" className="min-h-11" onClick={() => setOpenId(version.id)}>Bekijk</Button>}
                            {canManage && version.status !== 'voorbeeldsetlist' && version.id === detail.id && (
                              <Button type="button" variant="outline" className="min-h-11" disabled={busy} onClick={() => run(() => setLibraryStatus(detail.id, 'voorbeeldsetlist'), 'Gekozen als voorbeeld')}>Kies als voorbeeld</Button>
                            )}
                            {canManage && version.id === detail.id && (
                              <Button type="button" variant="outline" className="min-h-11" disabled={busy} onClick={() => run(() => detachVersion(detail.id), 'Los gekoppeld van de andere versies')}>Is een andere dienst</Button>
                            )}
                          </div>
                        </div>
                        <VersionDiff version={version} />
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {detail && <ReuseDialog setlist={detail} open={reuseOpen} onOpenChange={setReuseOpen} onDone={(result) => { setReuseOpen(false); onReused(result); }} />}
    </>
  );
};

export default SetlistDetail;
